import React, { useState, useCallback, useMemo } from 'react';
import { Navbar } from '../../components/Navbar';
import { NavigationRailGroup } from '../../components/NavigationRailGroup';
import { useMasterCrudTranslation } from '../../components/MasterCrud/useMasterCrudTranslation';
import type { LayoutBaseProps } from './LayoutBase.types';
import type { NavigationRailGroupMenuItem } from '../../components/NavigationRailGroup/NavigationRailGroup.types';

// ===== TRADUCCIONES INTERNAS (FALLBACK) =====
const internalTranslations: Record<string, any> = {
  es: {
    search: 'Buscar módulo',
    collapse: 'Colapsar',
    recent: 'Recientes',
    frequent: 'Opciones frecuentes',
    logout: 'Cerrar Sesión',
    account: 'Mi Cuenta',
    notifications: 'Notificaciones',
    billing: 'Facturación',
    darkMode: 'Modo Oscuro',
  },
  en: {
    search: 'Search module',
    collapse: 'Collapse',
    recent: 'Recent',
    frequent: 'Frequent options',
    logout: 'Sign Out',
    account: 'My Account',
    notifications: 'Notifications',
    billing: 'Billing',
    darkMode: 'Dark Mode',
  }
};

/**
 * LayoutBase - Layout base del sistema Siesa
 *
 * Layout reutilizable que implementa la estructura base con NavigationRail
 * lateral, Navbar superior y área de contenido dinámico. Diseñado para ser
 * usado como wrapper en diferentes vistas de la aplicación.
 *
 * **Composición:**
 * - Navbar: Barra superior con logo Siesa, título del producto y dropdown de usuario
 * - NavigationRailGroup: Rail de navegación lateral con iconos y búsqueda
 * - Área de contenido: Contenido dinámico pasado como children
 *
 * **Mejores prácticas implementadas:**
 * - Orden de modificadores: {responsive}:{dark}:{state}:{utility}
 * - Dark mode con estrategia 'class' (darkMode: 'class')
 * - Tokens de color consistentes con la documentación
 * - Responsive design mobile-first con breakpoints md:, lg:, xl:
 * - Composición con componentes del sistema (NO reimplementación)
 * - Type safety con TypeScript estricto
 *
 * @see docs/colors.md - Sistema de colores
 * @see docs/typography.md - Sistema tipográfico
 * @see docs/spacing.md - Sistema de espaciado
 * @see src/components/Navbar - Componente Navbar reutilizado
 * @see src/components/NavigationRailGroup - Componente NavigationRail reutilizado
 *
 * @example
 * ```tsx
 * // Uso básico
 * <LayoutBase
 *   productName="Mi Aplicación"
 *   siesaLogoPath="/images/logos/Siesa_Logosimbolo_Blanco.svg"
 *   siesaLogoWidth="100px"
 *   siesaLogoHeight="40px"
 *   userDropdown={{
 *     avatar: '/avatar.jpg',
 *     name: 'Carlos',
 *     email: 'carlos@siesa.com',
 *   }}
 *   navigationItems={navigationItems}
 * >
 *   <div>Mi contenido personalizado</div>
 * </LayoutBase>
 *
 * // Uso avanzado con props pass-through
 * <LayoutBase
 *   productName="Sistema Avanzado"
 *   navbarProps={{
 *     showNavigationButton: true,
 *     environmentBadge: "Desarrollo",
 *     navigationButtonProps: { type: 'outline', size: 'l' },
 *     notifications: { cart: 5, bell: true },
 *     onCartClick: () => openCart(),
 *     onNotificationsClick: () => openNotifications(),
 *   }}
 *   navigationRailProps={{
 *     showSearchButton: true,
 *     labels: { 
 *       searchPlaceholder: "Buscar módulos...",
 *       collapseButton: "Minimizar"
 *     },
 *     onItemClick: (item) => handleNavigation(item),
 *     onItemHover: (item) => showPreview(item),
 *   }}
 * >
 *   <div>Contenido con funcionalidad completa</div>
 * </LayoutBase>
 * ```
 */
export const LayoutBase: React.FC<LayoutBaseProps> = ({
  productName = 'Mi Aplicación',
  siesaLogoPath,
  siesaLogoWidth,
  siesaLogoHeight,
  userDropdown,
  navigationItems = [],
  children,
  className = '',
  contentClassName = '',
  hideSidebar = false,
  navbarProps = {},
  navigationRailProps = {},
  uiPrefix = 'layout',
  t: tProp,
  locale = 'es',
}) => {
  // ===== MOTOR DE TRADUCCIÓN =====
  const { t } = useMasterCrudTranslation({
    locale,
    uiPrefix,
    internalTranslations,
    tProp,
  });

  // ===== LÓGICA DE TRADUCCIÓN DE MENÚS =====
  const translateMenuItems = useCallback((items: NavigationRailGroupMenuItem[]): NavigationRailGroupMenuItem[] => {
    return items.map(item => ({
      ...item,
      label: t(`menu.${item.id}`, item.label),
      children: item.children ? translateMenuItems(item.children) : undefined
    }));
  }, [t]);

  const translatedNavigationItems = useMemo(() => 
    translateMenuItems(navigationItems), 
    [navigationItems, translateMenuItems]
  );

  // Estado local para el NavigationRail
  const [navState, setNavState] = useState<'collapsed' | 'expanded' | 'hover' | 'searcher'>('collapsed');

  // Handler para cambio de estado del NavigationRail
  const handleNavStateChange = (state: 'collapsed' | 'expanded' | 'hover' | 'searcher' | undefined) => {
    if (state) {
      setNavState(state);
    }
  };

  return (
    <div
      className={[
        // ===== BASE CONTAINER =====
        'h-screen', // Altura fija de pantalla completa
        'w-full',
        'bg-white', // Fondo blanco para Navbar y NavigationRail
        'dark:bg-dark-bg-primary',
        'flex',
        'flex-col',
        'overflow-hidden', // Prevenir scroll en contenedor principal
        className,
      ]
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()}
    >
      {/* ===== NAVBAR ===== */}
      <Navbar
        productName={productName}
        userDropdown={userDropdown}
        hideActionButtons
        showSiesaLogoLeading
        showBusinessLogo={false}
        siesaLogo={
          siesaLogoPath ? (
            <img 
              src={siesaLogoPath} 
              alt="Siesa" 
              className="object-contain"
              style={{
                width: siesaLogoWidth || '100%',
                height: siesaLogoHeight || '100%',
              }}
            />
          ) : undefined
        }
        className="bg-white dark:bg-dark-bg-primary"
        t={t}
        {...navbarProps}
      />

      {/* ===== LAYOUT: NAVIGATION RAIL + CONTENT ===== */}
      <div className="flex flex-1 overflow-hidden">
        {/* ===== NAVIGATION RAIL ===== */}
        {!hideSidebar && (
          <div style={{ position: 'relative', zIndex: 9999 }}>
          <NavigationRailGroup
            state={navState}
            items={translatedNavigationItems}
            showSearchButton={false}
            onStateChange={handleNavStateChange}
            t={t}
            labels={{
              searchPlaceholder: t('search', 'Buscar módulo'),
              collapseButton: t('collapse', 'Colapsar'),
              recentSection: t('recent', 'Recientes'),
              frequentSection: t('frequent', 'Opciones frecuentes'),
            }}
            {...navigationRailProps}
          />
          </div>
        )}

        {/* ===== CONTENT AREA ===== */}
        <div
          className={[
            'flex-1',
            'overflow-y-auto',
            'bg-gray-50',
            'dark:bg-dark-bg-primary',
            'p-6',
            'flex flex-col', // Asegurar que el contenido pueda ser flex y crecer
            contentClassName,
          ]
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim()}
        >
          <div className="w-full flex-1 flex flex-col">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};
